import { gql, useQuery } from '@apollo/client';
import { useCallback } from 'react';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ErrorInsightsListGroupByColumn } from 'src/lib/graphqlTypes/types';

import { BreakdownOption, GroupOption } from './GroupByButton';

const RouterLastSeenAtQuery = gql`
  query RouterLastSeenAtQuery($graphId: ID!, $variant: String!) {
    graph(id: $graphId) {
      id
      variant(name: $variant) {
        id
        routerLastSeenAt
      }
    }
  }
`;

const codeNameForEmptyCode = 'unknown';
const codeNameForEmptyPath = 'unknown';

/**
 * We need to translate the "empty" service name into a real display value,
 * so use the agent name as the service name when it's empty.
 * @returns a helper function to translate dimension values to display names.
 */
export function useDisplayNameTranslator() {
  const { routeParams } = useOperationInsightsFilter();

  const timeRange = routeParams.range ?? 'lastDay';

  const { timeFrom } = date.getAbsoluteDateBoundsWithResolution(timeRange, {
    from: routeParams.from,
    to: routeParams.to,
  });
  const graphRef = useGraphRef();

  const { data } = useQuery<GraphQLTypes.RouterLastSeenAtQuery>(
    RouterLastSeenAtQuery,
    {
      variables: {
        graphId: graphRef?.graphId as string,
        variant: graphRef?.graphVariant as string,
      },
    },
  );
  const routerLastSeenAt = data?.graph?.variant?.routerLastSeenAt?.toString();

  const routerLastSeenAtDate = routerLastSeenAt
    ? new Date(routerLastSeenAt)
    : undefined;

  const shouldUseRouterAsService = routerLastSeenAtDate
    ? routerLastSeenAtDate >= timeFrom
    : false;

  const agentNameForEmptyService = shouldUseRouterAsService
    ? 'Router'
    : 'GraphQL Server';

  const translateToDisplayName = useCallback(
    (
      dimensionValue: string | undefined,
      groupByDimension: GroupOption | BreakdownOption,
    ) => {
      switch (groupByDimension) {
        case ErrorInsightsListGroupByColumn.SERVICE_OR_AGENT:
          return dimensionValue || agentNameForEmptyService;
        case ErrorInsightsListGroupByColumn.CODE:
          return dimensionValue || codeNameForEmptyCode;
        case ErrorInsightsListGroupByColumn.PATH:
          return dimensionValue || codeNameForEmptyPath;
        default:
          assertUnreachable(groupByDimension);
      }
    },
    [agentNameForEmptyService],
  );

  return { translateToDisplayName };
}
